import type { FC } from 'react';
import { Avatar, Button, Col, Dropdown, Menu, Row, Space, Typography } from 'antd';
import type { IconType } from 'react-icons';
import {
    TbBallpen,
    TbBlockquote,
    TbChartInfographic,
    TbCheck,
    TbChecklist,
    TbCornerDownRight,
    TbDots,
    TbH1,
    TbH2,
    TbH3,
    TbH4,
    TbH5,
    TbH6,
    TbInfoCircle,
    TbLink,
    TbList,
    TbListNumbers,
    TbMessage,
    TbPageBreak,
    TbPhoto,
    TbSourceCode,
    TbTrash,
    TbWorld,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { MiddleDot, RelativeTimeTooltip } from '@/shared';
import { ActiveAvatarHoverDropdown } from '@/shared/components/dropdowns';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { WorkspaceResourceReferenceButton } from '@/shared/components/workspace-resources';
import type { CommentComposerValue, ReportComponent, ResourceActivityResourceType, ResourceComment } from '@/types';
import { dayjs, ellipsisStyling } from '@/utilities';

import CommentComposer from '../CommentComposer';
import CommentMessage from '../CommentMessage';
import { emitResourceCommentTargetFocusEvent } from '../resourceCommentEvents';
import { commenterName, commentTarget, investmentAnalysisHref, workspaceResourceHref } from './utils';

interface ResourceCommentCardProps {
    active?: boolean;
    comment: ResourceComment;
    onCreateReply?: (comment: ResourceComment, value: CommentComposerValue) => Promise<boolean | void> | boolean | void;
    onDelete?: (commentId: string) => void;
    onToggleResolved?: (comment: ResourceComment) => void;
    replies?: ResourceComment[];
    reportComponentsById?: Record<string, ReportComponent>;
    resourceId: string;
    resourceTitle?: string;
    resourceType: ResourceActivityResourceType;
    resourceWebsite?: string;
    showActions?: boolean;
    showReplyComposer?: boolean;
    submittingReply?: boolean;
}

const memoCommentNodeIconMap: Record<string, IconType> = {
    blockquote: TbBlockquote,
    bulletList: TbList,
    callout: TbInfoCircle,
    codeBlock: TbSourceCode,
    horizontalRule: TbPageBreak,
    image: TbPhoto,
    iframe: TbWorld,
    linkEmbed: TbLink,
    listItem: TbList,
    orderedList: TbListNumbers,
    paragraph: TbBallpen,
    reportComponent: TbChartInfographic,
    reportComponentEmbed: TbChartInfographic,
    taskList: TbChecklist,
    taskItem: TbChecklist,
    unfurledLink: TbLink,
    youtube: TbWorld,
};

const memoCommentTargetIcon = (nodeType?: string, rawTarget?: Record<string, unknown>) => {
    const rawAttrs = (rawTarget?.attrs ?? {}) as Record<string, unknown>;
    const headingIconByLevel: Record<number, IconType> = {
        1: TbH1,
        2: TbH2,
        3: TbH3,
        4: TbH4,
        5: TbH5,
        6: TbH6,
    };
    const Icon =
        nodeType === 'heading' && typeof rawAttrs.level === 'number'
            ? headingIconByLevel[rawAttrs.level]
            : nodeType
              ? memoCommentNodeIconMap[nodeType]
              : undefined;

    return Icon ? <Icon size='0.75rem' /> : undefined;
};

const ResourceCommentCard: FC<ResourceCommentCardProps> = ({
    active,
    comment,
    onCreateReply,
    onDelete,
    onToggleResolved,
    replies = [],
    reportComponentsById = {},
    resourceId,
    resourceTitle,
    resourceType,
    resourceWebsite,
    showActions = true,
    showReplyComposer = true,
    submittingReply,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const navigate = useNavigate();
    const user = comment.user ?? comment.member?.user;
    const commenterLabel = commenterName(comment);
    const commenterCollaborator = comment.member ? { member: comment.member } : user ? { user } : undefined;
    const target = commentTarget(comment, reportComponentsById);

    const focusTarget = () => {
        if (!target) {
            return;
        }

        const storedHref = typeof target.raw.href === 'string' ? target.raw.href : undefined;
        const fallbackHref =
            (resourceType === 'investments' ? investmentAnalysisHref(resourceId, comment.component) : undefined) ??
            workspaceResourceHref(resourceType, resourceId);
        const targetHref = storedHref?.startsWith('/') ? storedHref : fallbackHref;

        if (targetHref) {
            navigate(targetHref);
        }

        emitResourceCommentTargetFocusEvent({
            commentId: comment.id,
            component: comment.component,
            resourceId,
            resourceType,
            target: target.raw,
        });
    };
    const resolvedResourceTitle = resourceTitle?.trim() || target?.label;
    const nestedTargetContext = target
        ? [...target.breadcrumbs, target.label].filter(
              (label, index, labels) =>
                  label.localeCompare(resolvedResourceTitle ?? '', undefined, { sensitivity: 'accent' }) !== 0 &&
                  labels.indexOf(label) === index,
          )
        : [];

    const targetReference = target ? (
        target.type === 'text-selection' && target.quote ? (
            <div
                role='button'
                tabIndex={0}
                onClick={focusTarget}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        focusTarget();
                    }
                }}
                style={{
                    background: token.colorFillQuaternary,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    cursor: 'pointer',
                    marginBottom: '0.625rem',
                    padding: '0.625rem 0.75rem',
                }}
            >
                <Typography.Text
                    style={{
                        borderLeft: `3px solid ${token.colorBorder}`,
                        color: token.colorTextSecondary,
                        display: 'block',
                        paddingLeft: '0.625rem',
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {target.quote}
                </Typography.Text>
            </div>
        ) : (
            <div style={{ marginBottom: '0.625rem' }}>
                <WorkspaceResourceReferenceButton
                    ariaLabel={`Open ${[resolvedResourceTitle, ...nestedTargetContext].filter(Boolean).join(', ')}`}
                    nestedContext={nestedTargetContext}
                    nestedContextIcon={memoCommentTargetIcon(target.nodeType, target.raw)}
                    onClick={focusTarget}
                    resourceName={resolvedResourceTitle}
                    resourceType={resourceType}
                    resourceWebsite={resourceWebsite}
                />
            </div>
        )
    ) : null;

    const copyLink = () => {
        const value = `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}comment=${comment.id}`;

        navigator.clipboard.writeText(value);
        toast.success('Comment link copied');
    };

    const menu = (
        <DropdownMenuPanel>
            <Menu.Item key='copy-link' icon={<TbLink size='1rem' />} onClick={copyLink}>
                Copy Link to Comment
            </Menu.Item>
            {onToggleResolved && (
                <Menu.Item
                    key='resolve'
                    icon={comment.resolved ? <TbMessage size='1rem' /> : <TbCheck size='1rem' />}
                    onClick={() => onToggleResolved(comment)}
                >
                    {comment.resolved ? 'Reopen Comment' : 'Mark as Resolved'}
                </Menu.Item>
            )}
            {onDelete && (
                <>
                    <Menu.Divider />
                    <Menu.Item key='delete' danger icon={<TbTrash size='1rem' />} onClick={() => onDelete(comment.id)}>
                        Delete Comment
                    </Menu.Item>
                </>
            )}
        </DropdownMenuPanel>
    );

    return (
        <div
            data-resource-comment-id={comment.id}
            style={{
                background: active ? token.colorFillTertiary : undefined,
                borderRadius: token.borderRadius,
                padding: active ? '0.375rem' : undefined,
                transition: 'background-color 180ms ease',
                width: '100%',
            }}
        >
            <Row align='top' gutter={10} wrap={false}>
                <Col flex='none'>
                    {commenterCollaborator ? (
                        <ActiveAvatarHoverDropdown
                            collaborator={commenterCollaborator}
                            showResourceContext={false}
                            tooltipPlacement='bottomLeft'
                        >
                            <Avatar src={user?.avatar} size={24}>
                                {commenterLabel.charAt(0)}
                            </Avatar>
                        </ActiveAvatarHoverDropdown>
                    ) : (
                        <Avatar src={user?.avatar} size={24}>
                            {commenterLabel.charAt(0)}
                        </Avatar>
                    )}
                </Col>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <Row align='middle' wrap={false}>
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            {commenterCollaborator ? (
                                <ActiveAvatarHoverDropdown
                                    collaborator={commenterCollaborator}
                                    showResourceContext={false}
                                    tooltipPlacement='bottomLeft'
                                >
                                    <span style={{ ...ellipsisStyling, display: 'block' }}>{commenterLabel}</span>
                                </ActiveAvatarHoverDropdown>
                            ) : (
                                <Typography.Text style={{ ...ellipsisStyling, display: 'block' }}>
                                    {commenterLabel}
                                </Typography.Text>
                            )}
                        </Col>
                        <Col flex='none'>
                            <small style={{ color: token.colorTextTertiary }}>
                                <RelativeTimeTooltip date={comment.createdAt} />
                            </small>
                        </Col>
                        {showActions && (
                            <Col flex='none' style={{ marginLeft: '0.25rem' }}>
                                <Dropdown
                                    key='actions'
                                    trigger={['click']}
                                    placement='bottomRight'
                                    popupRender={() => menu}
                                >
                                    <Button
                                        size='small'
                                        type='text'
                                        icon={<TbDots style={{ color: token.colorTextTertiary }} />}
                                    />
                                </Dropdown>
                            </Col>
                        )}
                    </Row>
                    {targetReference}
                    <Typography.Paragraph style={{ marginBottom: '0.25rem' }}>
                        <CommentMessage message={comment.message} metadata={comment.metadata} />
                    </Typography.Paragraph>
                    <small style={{ color: token.colorTextTertiary }}>
                        {dayjs(comment.createdAt).format('MMM D, YYYY h:mm A')}
                        {comment.resolved && (
                            <>
                                <MiddleDot horizontalMargin='0.375rem' />
                                Resolved
                            </>
                        )}
                    </small>
                    {replies.length > 0 && (
                        <Space direction='vertical' size={8} style={{ marginTop: '0.75rem', width: '100%' }}>
                            {replies.map((reply) => {
                                const replyUser = reply.user ?? reply.member?.user;
                                const replyLabel = commenterName(reply);
                                const replyCollaborator = reply.member
                                    ? { member: reply.member }
                                    : replyUser
                                      ? { user: replyUser }
                                      : undefined;

                                return (
                                    <Row
                                        key={reply.id}
                                        align='top'
                                        gutter={8}
                                        wrap={false}
                                        data-resource-comment-id={reply.id}
                                    >
                                        <Col flex='none'>
                                            <TbCornerDownRight
                                                size='1rem'
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    marginTop: '0.25rem',
                                                }}
                                            />
                                        </Col>
                                        <Col flex='none'>
                                            {replyCollaborator ? (
                                                <ActiveAvatarHoverDropdown
                                                    collaborator={replyCollaborator}
                                                    showResourceContext={false}
                                                    tooltipPlacement='bottomLeft'
                                                >
                                                    <Avatar src={replyUser?.avatar} size={20}>
                                                        {replyLabel.charAt(0)}
                                                    </Avatar>
                                                </ActiveAvatarHoverDropdown>
                                            ) : (
                                                <Avatar src={replyUser?.avatar} size={20}>
                                                    {replyLabel.charAt(0)}
                                                </Avatar>
                                            )}
                                        </Col>
                                        <Col flex='auto' style={{ minWidth: 0 }}>
                                            <Row align='middle' gutter={6} wrap={false}>
                                                <Col flex='auto' style={{ minWidth: 0 }}>
                                                    {replyCollaborator ? (
                                                        <ActiveAvatarHoverDropdown
                                                            collaborator={replyCollaborator}
                                                            showResourceContext={false}
                                                            tooltipPlacement='bottomLeft'
                                                        >
                                                            <span
                                                                style={{
                                                                    ...ellipsisStyling,
                                                                    display: 'block',
                                                                    fontSize: token.fontSizeSM,
                                                                }}
                                                            >
                                                                {replyLabel}
                                                            </span>
                                                        </ActiveAvatarHoverDropdown>
                                                    ) : (
                                                        <Typography.Text
                                                            style={{
                                                                ...ellipsisStyling,
                                                                display: 'block',
                                                                fontSize: token.fontSizeSM,
                                                            }}
                                                        >
                                                            {replyLabel}
                                                        </Typography.Text>
                                                    )}
                                                </Col>
                                                <Col flex='none'>
                                                    <small style={{ color: token.colorTextTertiary }}>
                                                        <RelativeTimeTooltip date={reply.createdAt} />
                                                    </small>
                                                </Col>
                                            </Row>
                                            <Typography.Paragraph
                                                style={{
                                                    color: token.colorTextSecondary,
                                                    marginBottom: 0,
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                <CommentMessage message={reply.message} metadata={reply.metadata} />
                                            </Typography.Paragraph>
                                        </Col>
                                    </Row>
                                );
                            })}
                        </Space>
                    )}
                    {showReplyComposer && onCreateReply && (
                        <div style={{ marginTop: '0.75rem' }}>
                            <CommentComposer
                                minHeight='2.25rem'
                                maxHeight='6rem'
                                onSubmit={(value) => onCreateReply(comment, value)}
                                placeholder='Reply...'
                                resourceId={resourceId}
                                resourceType={resourceType}
                                submitting={submittingReply}
                            />
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default ResourceCommentCard;
